import { useLocale } from '../i18n';
import { getLegalContent, type LegalDocument } from '../legal';
import type { NavView } from '../components/Layout';

type LegalView = 'terms' | 'privacy' | 'cookies' | 'custodia';

interface LegalProps {
  view: LegalView;
  onNavigate?: (view: NavView) => void;
  backView?: NavView;
  // Issue #504: 'custodia' exige login — controla a aba na sub-nav e o
  // fallback do documento caso a view seja alcancada sem sessao.
  isLoggedIn?: boolean;
}

function Legal({ view, onNavigate, backView, isLoggedIn }: LegalProps) {
  const { locale, t } = useLocale();
  const content = getLegalContent(locale);
  // Issue #504 (defesa em profundidade): se 'custodia' for renderizada sem
  // login (ex.: gate do App contornado), mostra 'terms' em vez do termo de
  // custodia. O gate em App.tsx ja impede esse caminho; aqui e so fallback.
  const doc: LegalDocument = content[view === 'custodia' && !isLoggedIn ? 'terms' : view];

  return (
    <div className="mx-auto max-w-3xl">
      {/* Issue #491: botao Voltar — a SPA nao tem historico de navegacao,
          entao a pagina legal precisa de retorno explicito para a view
          de origem (ex.: tela de certificados ao abrir o termo de custodia). */}
      {backView && (
        <button
          type="button"
          onClick={() => onNavigate?.(backView)}
          className="mb-4 cursor-pointer text-sm font-medium text-brand-600 hover:text-brand-900"
        >
          ← {t('common.legal.back')}
        </button>
      )}
      {/* Sub-nav for switching between legal documents */}
      <nav className="mb-6 flex flex-wrap gap-4 text-sm font-medium text-brand-600">
        <button
          type="button"
          onClick={() => onNavigate?.('terms')}
          className={`cursor-pointer hover:text-brand-900 ${view === 'terms' ? 'font-semibold text-brand-900 border-b-2 border-brand-700' : ''}`}
        >
          {t('common.legal.terms')}
        </button>
        <button
          type="button"
          onClick={() => onNavigate?.('privacy')}
          className={`cursor-pointer hover:text-brand-900 ${view === 'privacy' ? 'font-semibold text-brand-900 border-b-2 border-brand-700' : ''}`}
        >
          {t('common.legal.privacy')}
        </button>
        <button
          type="button"
          onClick={() => onNavigate?.('cookies')}
          className={`cursor-pointer hover:text-brand-900 ${view === 'cookies' ? 'font-semibold text-brand-900 border-b-2 border-brand-700' : ''}`}
        >
          {t('common.legal.cookies')}
        </button>
        {/* Issue #504: aba de custodia so aparece para usuario logado —
            o documento exige autenticacao. */}
        {isLoggedIn && (
          <button
            type="button"
            onClick={() => onNavigate?.('custodia')}
            className={`cursor-pointer hover:text-brand-900 ${view === 'custodia' ? 'font-semibold text-brand-900 border-b-2 border-brand-700' : ''}`}
          >
            {t('common.legal.custodia')}
          </button>
        )}
      </nav>

      <h1 className="mb-2 text-2xl font-bold text-brand-900">{doc.title}</h1>
      <p className="mb-2 text-sm text-brand-500">
        {doc.version} — {doc.effectiveDate}
      </p>
      <div className="mb-6 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
        {doc.draftNotice}
      </div>

      <div className="space-y-6">
        {doc.sections.map((section, i) => (
          <section key={i}>
            <h2 className="mb-2 text-lg font-semibold text-brand-800">{section.heading}</h2>
            {section.paragraphs?.map((p, j) => (
              <p key={j} className="mb-2 leading-relaxed text-brand-700">{p}</p>
            ))}
            {section.list && (
              <ul className="ml-6 list-disc space-y-1 text-brand-700">
                {section.list.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

export default Legal;
